import { configureAlgolia, setAlgoliaUserToken } from "@repo/shopifyV2/algolia/client";

import { DOMAIN, PREVIEW_DOMAIN } from "@const/urls";

import type { ICustomerStore } from "@store/customer";
import { useCustomerStore } from "@store/customer";

/**
 * 스토어별 Algolia 자격 증명.
 *
 * KR / INTL / JP 가 각각 별도 Algolia 앱이라, Apollo 와 동일하게 도메인으로 스토어를 판별해
 * 모듈 로드 시점에 한 번 주입한다. (lib/apollo.ts 의 언어 판별과 같은 규칙)
 */
const KEYS = {
  ko: {
    appId: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_APP_ID,
    searchKey: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_SEARCH_KEY,
    indexName: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_INDEX_NAME,
    replicas: {
      priceAsc: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_INDEX_PRICE_ASC,
      priceDesc: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_INDEX_PRICE_DESC,
      createdDesc: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_INDEX_CREATED_DESC,
      bestSelling: import.meta.env.VITE_KR_REACT_APP_ALGOLIA_INDEX_BEST_SELLING,
    },
  },
  en: {
    appId: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_APP_ID,
    searchKey: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_SEARCH_KEY,
    indexName: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_INDEX_NAME,
    replicas: {
      priceAsc: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_INDEX_PRICE_ASC,
      priceDesc: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_INDEX_PRICE_DESC,
      createdDesc: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_INDEX_CREATED_DESC,
      bestSelling: import.meta.env.VITE_INTL_REACT_APP_ALGOLIA_INDEX_BEST_SELLING,
    },
  },
  jp: {
    appId: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_APP_ID,
    searchKey: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_SEARCH_KEY,
    indexName: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_INDEX_NAME,
    replicas: {
      priceAsc: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_INDEX_PRICE_ASC,
      priceDesc: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_INDEX_PRICE_DESC,
      createdDesc: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_INDEX_CREATED_DESC,
      bestSelling: import.meta.env.VITE_JP_REACT_APP_ALGOLIA_INDEX_BEST_SELLING,
    },
  },
} as const;

function resolveLang(): keyof typeof KEYS {
  const stored = localStorage.getItem("i18nextLng");
  const href = window.location.href;

  if (href.includes("localhost") || window.location.hostname.includes("ngrok")) {
    return stored && stored in KEYS ? (stored as keyof typeof KEYS) : "ko";
  }

  if (href.includes(DOMAIN.en) || href.includes(PREVIEW_DOMAIN.en)) return "en";
  if (href.includes(DOMAIN.jp) || href.includes(PREVIEW_DOMAIN.jp)) return "jp";

  return "ko";
}

export const ALGOLIA_LANG = resolveLang();

/** 이 스토어에서 Algolia 검색을 쓰는지. 키가 비어 있으면 기존 Shopify 검색을 쓴다. */
export const ALGOLIA_ENABLED = Boolean(KEYS[ALGOLIA_LANG].appId && KEYS[ALGOLIA_LANG].searchKey && KEYS[ALGOLIA_LANG].indexName);

if (ALGOLIA_ENABLED) configureAlgolia({ ...KEYS[ALGOLIA_LANG] });

/**
 * 로그인한 고객 ID. JP 는 Customer Account API 로 로그인해 caaCustomer 에, KR / INTL 은 customer 에 있다.
 * App.tsx 의 loggedInCustomerId 와 같은 규칙이다.
 */
const customerIdOf = (state: ICustomerStore) => (ALGOLIA_LANG === "jp" ? state.caaCustomer?.id : state.customer.id);

// 로그인한 고객 ID 를 모든 Algolia 검색 요청에 userToken 으로 싣는다(Personalization 대비). 로그인 · 로그아웃마다 갱신된다.
setAlgoliaUserToken(customerIdOf(useCustomerStore.getState()));
useCustomerStore.subscribe((state) => {
  setAlgoliaUserToken(customerIdOf(state));
});
